import {
  Fragment,
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  FORM_CONTROL_SELECT_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { ClientTypePicker } from '@/components/clients/ClientTypePicker'
import { FormExpandLink } from '@/components/clients/FormExpandLink'
import { SireneLookupField } from '@/components/clients/SireneLookupField'
import { AddressFields, type AddressFieldsChange } from '@/components/shared'
import {
  clientFormSchema,
  emptyContact,
  joinIndividualContactName,
  splitContactName,
  type ClientFormValues,
} from '@/lib/validations/clients'
import {
  formatPhoneInput,
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@/lib/phone'
import { maxContactsForType } from '@/lib/clientContacts'
import { useClient, useCreateClient, useUpdateClient } from '@/hooks/useClients'
import { getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import { DEFAULT_COUNTRY, resolveCountryLabel } from '@/lib/constants'
import type { Client, ClientType, CreateClientRequest } from '@/types'

const INDIVIDUAL_TYPE: ClientType = 'individual'

const PROPERTY_TYPES = ['apartment', 'house', 'office', 'warehouse']
const SOURCES = [
  'direct_call',
  'website',
  'recommendation',
  'google_reviews',
  'on_site_visit',
  'partner',
  'other',
] as const
const SOURCE_KEYS = SOURCES as readonly string[]

const fieldControlClass = cn('leading-[1.4]', FORM_CONTROL_CLASS)

const selectControlClass = cn('leading-[1.4]', FORM_CONTROL_SELECT_CLASS)

const fieldInputClass = cn(
  'h-10 rounded-xl px-4 py-3 placeholder:font-normal placeholder:text-[#6c7285]',
  fieldControlClass
)

const selectTriggerClass = cn(
  'h-10 rounded-xl px-4 py-3 data-[placeholder]:font-normal [&>span]:font-normal data-[placeholder]:[&>span]:font-normal [&>span>span]:font-normal data-[placeholder]:[&>span>span]:font-normal',
  selectControlClass
)

const textareaClass = cn(
  'min-h-20 rounded-xl px-4 py-3 placeholder:font-normal placeholder:text-[#6c7285]',
  fieldControlClass
)

const wrapFieldClass = 'w-full min-w-[140px] flex-1 basis-[calc(50%-8px)]'

function hasAccessDetails(values: Partial<ClientFormValues>): boolean {
  return Boolean(
    values.address_additional?.trim() ||
    values.access_code?.trim() ||
    values.floor?.trim() ||
    values.access_information?.trim()
  )
}

function sourceSelectValue(source: string | undefined): string {
  if (!source) return ''
  if (SOURCE_KEYS.includes(source) && source !== 'other') return source
  return 'other'
}

function SectionHeading({ number, title }: { number: number; title: string }) {
  return (
    <div className='flex items-center gap-3'>
      <span className='relative flex size-6 shrink-0 items-center justify-center rounded-md bg-[#ff5a1f] text-xs font-medium leading-4 text-white'>
        {number}
      </span>
      <h2 className='font-heading text-sm font-semibold normal-case leading-[1.2] text-[#171a20]'>
        {title}
      </h2>
    </div>
  )
}

function Section({
  number,
  title,
  children,
}: {
  number: number
  title: string
  children: ReactNode
}) {
  return (
    <section className='flex w-full min-w-0 flex-col overflow-clip rounded-2xl border border-[#e7e9ef] bg-white shadow-[0px_1px_8px_-1px_rgba(38,45,66,0.1),0px_1px_4px_-1px_rgba(38,45,66,0.05)]'>
      <div className='flex items-center px-4 pb-2 pt-4'>
        <SectionHeading number={number} title={title} />
      </div>
      <div className='flex flex-col gap-4 px-4 pb-4 pt-2'>{children}</div>
    </section>
  )
}

function RequiredLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='text-xs font-medium normal-case leading-4 text-[#171a20]'
    >
      {children}
      <span className='text-[#d63b33]'> *</span>
    </Label>
  )
}

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='text-xs font-medium normal-case leading-4 text-[#171a20]'
    >
      {children}
    </Label>
  )
}

const iconInputWrapperClass = cn(
  'flex h-10 w-full items-center gap-2 rounded-xl border px-4 py-3',
  fieldControlClass,
  FORM_CONTROL_FOCUS_WITHIN_CLASS
)

const iconInputInnerClass =
  'min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:font-normal placeholder:text-[#6c7285] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'

const IconInput = forwardRef<
  HTMLInputElement,
  { icon: LucideIcon } & ComponentProps<'input'>
>(({ icon: Icon, className, ...props }, ref) => (
  <div className={cn(iconInputWrapperClass, className)}>
    <Icon className='h-4 w-4 shrink-0 text-[#1E1E1E]' aria-hidden='true' />
    <input ref={ref} className={iconInputInnerClass} {...props} />
  </div>
))
IconInput.displayName = 'IconInput'

function WrapField({ children }: { children: ReactNode }) {
  return <div className={wrapFieldClass}>{children}</div>
}

export interface ClientFormProps {
  mode: 'create' | 'edit'
  clientId?: string
  onCancel: () => void
  onSuccess: (client: Client) => void
}

export function ClientForm({
  mode,
  clientId,
  onCancel,
  onSuccess,
}: ClientFormProps) {
  const { t } = useTranslation('clients')
  const editing = mode === 'edit'

  const {
    data: clientData,
    isLoading,
    isError,
    error,
  } = useClient(editing ? clientId : undefined)
  const create = useCreateClient()
  const update = useUpdateClient(clientId ?? '')

  const editValues = useMemo<ClientFormValues | undefined>(() => {
    if (!editing || !clientData?.data) return undefined
    const c = clientData.data
    const names = splitContactName(c.name)
    const contacts =
      c.contacts && c.contacts.length > 0
        ? c.contacts.map(contact => ({
            id: contact.id,
            full_name: contact.full_name ?? '',
            role: contact.role ?? '',
            phone: formatPhoneNumber(contact.phone),
            email: contact.email ?? '',
          }))
        : [emptyContact()]
    return {
      type: c.type,
      name: c.type === INDIVIDUAL_TYPE ? '' : (c.name ?? ''),
      first_name: c.type === INDIVIDUAL_TYPE ? names.first_name : '',
      last_name: c.type === INDIVIDUAL_TYPE ? names.last_name : '',
      contacts,
      siret: c.siret ?? '',
      address: c.address ?? '',
      address_additional: c.address_additional ?? '',
      postal_code: c.postal_code ?? '',
      city: c.city ?? '',
      country: resolveCountryLabel(c.country),
      latitude: c.latitude ?? undefined,
      longitude: c.longitude ?? undefined,
      property_type: c.property_type ?? '',
      source: c.source ?? '',
      status: c.status ?? 'active',
      notes: c.notes ?? '',
      access_code: c.access_code ?? '',
      floor: c.floor ?? '',
      access_information: c.access_information ?? '',
    }
  }, [editing, clientData])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitted },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      type: INDIVIDUAL_TYPE,
      status: 'active',
      country: DEFAULT_COUNTRY,
      name: '',
      first_name: '',
      last_name: '',
      contacts: [emptyContact()],
      siret: '',
      address: '',
      address_additional: '',
      city: '',
      postal_code: '',
      property_type: '',
      source: '',
      notes: '',
      access_code: '',
      floor: '',
      access_information: '',
    },
    ...(editing && editValues ? { values: editValues } : {}),
  })

  const selectedType = watch('type') ?? INDIVIDUAL_TYPE
  const isBusiness = selectedType !== INDIVIDUAL_TYPE
  const source = watch('source') ?? ''
  const propertyType = watch('property_type') ?? ''
  const status = watch('status') ?? 'active'

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  })

  const [showAccessDetails, setShowAccessDetails] = useState(false)
  const [expandInitialized, setExpandInitialized] = useState(!editing)

  useEffect(() => {
    if (!editing || !editValues || expandInitialized) return
    setShowAccessDetails(hasAccessDetails(editValues))
    setExpandInitialized(true)
  }, [editing, editValues, expandInitialized])

  useEffect(() => {
    if (editing && isError) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('form.loadError')),
      })
      onCancel()
    }
  }, [editing, isError, error, onCancel, t])

  const onAddressChange = (next: AddressFieldsChange) => {
    const opts = { shouldDirty: true, shouldValidate: isSubmitted }
    if (next.country !== undefined) setValue('country', next.country, opts)
    if (next.city !== undefined) setValue('city', next.city, opts)
    if (next.postalCode !== undefined)
      setValue('postal_code', next.postalCode, opts)
    if (next.address !== undefined) setValue('address', next.address, opts)
    if ('latitude' in next)
      setValue('latitude', next.latitude, { shouldDirty: true })
    if ('longitude' in next)
      setValue('longitude', next.longitude, { shouldDirty: true })
  }

  const displaySourceSelect = sourceSelectValue(source)

  const onSubmit = async (values: ClientFormValues) => {
    const isBusinessValue = values.type !== INDIVIDUAL_TYPE
    const individualName = joinIndividualContactName(
      values.first_name,
      values.last_name
    )

    const payload: CreateClientRequest = {
      type: values.type,
      name: isBusinessValue ? (values.name ?? '') : individualName,
      company_name: isBusinessValue ? values.name || undefined : undefined,
      contacts: values.contacts.map(contact => ({
        ...(contact.id ? { id: contact.id } : {}),
        full_name: contact.full_name || undefined,
        role: contact.role || undefined,
        phone: normalizePhoneNumber(contact.phone),
        email: contact.email?.trim() || undefined,
      })),
      siret: values.siret || undefined,
      country: values.country,
      city: values.city,
      postal_code: values.postal_code,
      address: values.address,
      address_additional: values.address_additional || undefined,
      latitude: values.latitude,
      longitude: values.longitude,
      source:
        values.source && values.source !== 'other' ? values.source : undefined,
      status: values.status || undefined,
      notes: values.notes || undefined,
      property_type: values.property_type || undefined,
      access_code: values.access_code || undefined,
      floor: values.floor || undefined,
      access_information: values.access_information || undefined,
    }
    try {
      if (editing && clientId) {
        const res = await update.mutateAsync(payload)
        toast({ variant: 'success', title: t('form.updateSuccess') })
        onSuccess(res.data ?? ({ id: clientId, ...payload } as Client))
      } else {
        const res = await create.mutateAsync(payload)
        toast({ variant: 'success', title: t('form.createSuccess') })
        if (res.data) onSuccess(res.data)
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          editing ? t('form.updateError') : t('form.createError')
        ),
      })
    }
  }

  const errClass = (has: unknown) => cn(Boolean(has) && 'border-destructive')

  const pending = create.isPending || update.isPending
  const submitLabel = editing
    ? t('form.saveChanges')
    : t('form.registerNewCustomer')

  if (editing && isLoading) {
    return (
      <div className='flex flex-col gap-4 p-6'>
        <Skeleton className='h-40 w-full rounded-2xl' />
        <Skeleton className='h-32 w-full rounded-2xl' />
        <Skeleton className='h-48 w-full rounded-2xl' />
      </div>
    )
  }

  return (
    <form
      id='client-form'
      onSubmit={handleSubmit(onSubmit)}
      className='flex min-h-0 flex-1 flex-col'
    >
      <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-[#f4f5f8] p-6'>
        <Section number={1} title={t('form.identity')}>
          <div className='space-y-1'>
            <RequiredLabel>{t('form.clientType')}</RequiredLabel>
            <ClientTypePicker
              value={selectedType}
              onChange={value => {
                setValue('type', value, { shouldValidate: true })
                const current = getValues('contacts')
                const max = maxContactsForType(value)
                let next =
                  current.length > max ? current.slice(0, max) : current
                if (next.length === 0) next = [emptyContact()]
                setValue('contacts', next, { shouldValidate: true })
              }}
            />
          </div>

          {isBusiness ? (
            <>
              <div className='space-y-1'>
                <FieldLabel>{t('form.companyLookup')}</FieldLabel>
                <SireneLookupField
                  inputClassName={fieldInputClass}
                  onSelect={c => {
                    const opts = {
                      shouldDirty: true,
                      shouldValidate: isSubmitted,
                    }
                    setValue('name', c.name, opts)
                    setValue('siret', c.siret, opts)
                    if (c.address) setValue('address', c.address, opts)
                    if (c.postal_code)
                      setValue('postal_code', c.postal_code, opts)
                    if (c.city) setValue('city', c.city, opts)
                  }}
                />
              </div>
              <div className='space-y-1'>
                <RequiredLabel htmlFor='name'>
                  {t('form.companyName')}
                </RequiredLabel>
                <Input
                  id='name'
                  aria-required='true'
                  placeholder={t('form.companyNamePlaceholder')}
                  className={cn(fieldInputClass, errClass(errors.name))}
                  {...register('name')}
                />
                {errors.name && (
                  <p className='text-xs text-destructive'>
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div className='space-y-1'>
                <FieldLabel htmlFor='siret'>{t('form.siret')}</FieldLabel>
                <Input
                  id='siret'
                  placeholder={t('form.enterHere')}
                  className={cn(fieldInputClass, errClass(errors.siret))}
                  {...register('siret')}
                />
              </div>
            </>
          ) : (
            <div className='flex flex-wrap gap-4'>
              <WrapField>
                <div className='space-y-1'>
                  <RequiredLabel htmlFor='last_name'>
                    {t('form.lastName')}
                  </RequiredLabel>
                  <Input
                    id='last_name'
                    aria-required='true'
                    placeholder={t('form.enterHere')}
                    className={cn(fieldInputClass, errClass(errors.last_name))}
                    {...register('last_name')}
                  />
                  {errors.last_name && (
                    <p className='text-xs text-destructive'>
                      {errors.last_name.message}
                    </p>
                  )}
                </div>
              </WrapField>
              <WrapField>
                <div className='space-y-1'>
                  <RequiredLabel htmlFor='first_name'>
                    {t('form.firstName')}
                  </RequiredLabel>
                  <Input
                    id='first_name'
                    aria-required='true'
                    placeholder={t('form.enterHere')}
                    className={cn(fieldInputClass, errClass(errors.first_name))}
                    {...register('first_name')}
                  />
                  {errors.first_name && (
                    <p className='text-xs text-destructive'>
                      {errors.first_name.message}
                    </p>
                  )}
                </div>
              </WrapField>
            </div>
          )}
        </Section>

        <Section number={2} title={t('form.contact')}>
          {fields.map((field, index) => {
            const contactErrors = errors.contacts?.[index]
            const phoneField = register(`contacts.${index}.phone`)
            const phoneLabel = t(index === 0 ? 'form.phone1' : 'form.phone')
            const emailLabel = t(index === 0 ? 'form.email1' : 'form.email')
            return (
              <Fragment key={field.id}>
                <div
                  className={cn(
                    'flex flex-col gap-4',
                    index > 0 && 'border-t border-dashed border-[#e7e9ef] pt-4'
                  )}
                >
                  <input type='hidden' {...register(`contacts.${index}.id`)} />
                  <div className='flex items-start justify-between gap-2'>
                    <div className='min-w-0 flex-1 space-y-1'>
                      <RequiredLabel htmlFor={`contacts.${index}.full_name`}>
                        {t('form.fullName')}
                      </RequiredLabel>
                      <Input
                        id={`contacts.${index}.full_name`}
                        aria-required='true'
                        placeholder={t('form.enterHere')}
                        className={cn(
                          fieldInputClass,
                          errClass(contactErrors?.full_name)
                        )}
                        {...register(`contacts.${index}.full_name`)}
                      />
                      {contactErrors?.full_name && (
                        <p className='text-xs text-destructive'>
                          {contactErrors.full_name.message}
                        </p>
                      )}
                    </div>
                    {fields.length > 1 && (
                      <button
                        type='button'
                        className='mt-6 rounded-lg p-2 text-[#3d434e] hover:bg-[#f4f5f8]'
                        aria-label={t('form.removeContact')}
                        onClick={() => remove(index)}
                      >
                        <Trash2 className='size-4' />
                      </button>
                    )}
                  </div>
                  <div className='space-y-1'>
                    <FieldLabel htmlFor={`contacts.${index}.role`}>
                      {t('form.role')}
                    </FieldLabel>
                    <Input
                      id={`contacts.${index}.role`}
                      placeholder={t('form.rolePlaceholder')}
                      className={fieldInputClass}
                      {...register(`contacts.${index}.role`)}
                    />
                  </div>

                  <div className='flex flex-wrap gap-4'>
                    <WrapField>
                      <div className='space-y-1'>
                        <RequiredLabel htmlFor={`contacts.${index}.phone`}>
                          {phoneLabel}
                        </RequiredLabel>
                        <IconInput
                          icon={Phone}
                          id={`contacts.${index}.phone`}
                          type='tel'
                          aria-required='true'
                          placeholder={t('form.phonePlaceholder')}
                          className={errClass(contactErrors?.phone)}
                          {...phoneField}
                          onChange={event => {
                            event.target.value = formatPhoneInput(
                              event.target.value
                            )
                            void phoneField.onChange(event)
                          }}
                        />
                        {contactErrors?.phone && (
                          <p className='text-xs text-destructive'>
                            {contactErrors.phone.message}
                          </p>
                        )}
                      </div>
                    </WrapField>
                    <WrapField>
                      <div className='space-y-1'>
                        <FieldLabel htmlFor={`contacts.${index}.email`}>
                          {emailLabel}
                        </FieldLabel>
                        <IconInput
                          icon={Mail}
                          id={`contacts.${index}.email`}
                          type='email'
                          placeholder={t('form.emailPlaceholder')}
                          className={errClass(contactErrors?.email)}
                          {...register(`contacts.${index}.email`)}
                        />
                        {contactErrors?.email && (
                          <p className='text-xs text-destructive'>
                            {contactErrors.email.message}
                          </p>
                        )}
                      </div>
                    </WrapField>
                  </div>
                </div>
              </Fragment>
            )
          })}

          {fields.length < maxContactsForType(selectedType) && (
            <button
              type='button'
              className='inline-flex items-center gap-2 self-start rounded-xl p-3 text-sm font-medium normal-case leading-none text-[#ff5a1f] hover:bg-[#f4f5f8]'
              onClick={() => append(emptyContact())}
            >
              <Plus className='size-4' />
              {t('form.addContact')}
            </button>
          )}
        </Section>

        <Section number={3} title={t('form.address')}>
          <AddressFields
            required
            layout='stacked'
            className='[&_label]:font-medium [&_label]:normal-case [&_label]:text-[#171a20]'
            controlClassName={fieldControlClass}
            selectControlClassName={selectControlClass}
            country={watch('country') ?? DEFAULT_COUNTRY}
            city={watch('city') ?? ''}
            postalCode={watch('postal_code') ?? ''}
            address={watch('address') ?? ''}
            onChange={onAddressChange}
            translationNs='clients'
            formatCountryLabel={c => t(`form.countries.${c}`)}
            errors={{
              city: errors.city?.message,
              address: errors.address?.message,
              postalCode: errors.postal_code?.message,
              country: errors.country?.message,
            }}
            countryTrailingSlot={
              <div className='space-y-1'>
                <FieldLabel>{t('form.propertyType')}</FieldLabel>
                <Select
                  value={propertyType || undefined}
                  onValueChange={v => {
                    if (!v) return
                    setValue('property_type', v)
                  }}
                >
                  <SelectTrigger className={selectTriggerClass}>
                    <SelectValue placeholder={t('form.selectType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(p => (
                      <SelectItem key={p} value={p}>
                        {t(`form.propertyTypes.${p}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            }
          />

          <FormExpandLink
            expanded={showAccessDetails}
            onToggle={() => setShowAccessDetails(v => !v)}
            expandLabel={t('form.accessDetailsExpand')}
            hideLabel={t('form.hide')}
            className='self-start text-sm font-medium normal-case'
          />

          {showAccessDetails && (
            <>
              <div className='space-y-1'>
                <FieldLabel htmlFor='address_additional'>
                  {t('form.addressDetails')}
                </FieldLabel>
                <IconInput
                  icon={MapPin}
                  id='address_additional'
                  placeholder={t('form.addressSearchPlaceholder')}
                  {...register('address_additional')}
                />
              </div>
              <div className='flex flex-wrap gap-4'>
                <WrapField>
                  <div className='space-y-1'>
                    <FieldLabel htmlFor='access_code'>
                      {t('form.accessCode')}
                    </FieldLabel>
                    <Input
                      id='access_code'
                      placeholder={t('form.enterHere')}
                      className={fieldInputClass}
                      {...register('access_code')}
                    />
                  </div>
                </WrapField>
                <WrapField>
                  <div className='space-y-1'>
                    <FieldLabel htmlFor='floor'>{t('form.floor')}</FieldLabel>
                    <Input
                      id='floor'
                      placeholder={t('form.enterHere')}
                      className={fieldInputClass}
                      {...register('floor')}
                    />
                  </div>
                </WrapField>
              </div>
              <div className='space-y-1'>
                <FieldLabel htmlFor='access_information'>
                  {t('form.accessInformation')}
                </FieldLabel>
                <Textarea
                  id='access_information'
                  rows={3}
                  placeholder={t('form.accessInfoPlaceholder')}
                  className={textareaClass}
                  {...register('access_information')}
                />
              </div>
            </>
          )}
        </Section>

        <Section number={4} title={t('form.metadata')}>
          <div className='flex flex-wrap gap-4'>
            <WrapField>
              <div className='space-y-1'>
                <FieldLabel>{t('form.clientSource')}</FieldLabel>
                <Select
                  value={displaySourceSelect || undefined}
                  onValueChange={v => {
                    if (!v) return
                    if (v === 'other') {
                      setValue('source', 'other')
                    } else {
                      setValue('source', v)
                    }
                  }}
                >
                  <SelectTrigger className={selectTriggerClass}>
                    <SelectValue placeholder={t('form.selectSource')} />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => (
                      <SelectItem key={s} value={s}>
                        {t(`form.sources.${s}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {displaySourceSelect === 'other' && (
                  <Input
                    className={cn('mt-2', fieldInputClass)}
                    value={source === 'other' ? '' : source}
                    onChange={e =>
                      setValue(
                        'source',
                        e.target.value.trim() ? e.target.value : 'other'
                      )
                    }
                    placeholder={t('form.sourceOtherPlaceholder')}
                    maxLength={100}
                  />
                )}
              </div>
            </WrapField>
            <WrapField>
              <div className='space-y-1'>
                <FieldLabel>{t('form.clientStatus')}</FieldLabel>
                <Select
                  value={status || 'active'}
                  onValueChange={v => {
                    if (!v) return
                    setValue('status', v)
                  }}
                >
                  <SelectTrigger className={selectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='active'>{t('form.active')}</SelectItem>
                    <SelectItem value='inactive'>
                      {t('form.inactive')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </WrapField>
          </div>
          <div className='space-y-1'>
            <FieldLabel htmlFor='notes'>{t('form.note')}</FieldLabel>
            <Textarea
              id='notes'
              rows={3}
              placeholder={t('form.notePlaceholder')}
              className={textareaClass}
              {...register('notes')}
            />
          </div>
        </Section>
      </div>

      <div className='tf-right-drawer-footer justify-end'>
        <Button
          type='button'
          variant='outline'
          className='rounded-lg px-3 py-3 text-sm font-medium normal-case text-[#171a20]'
          onClick={onCancel}
          disabled={pending}
        >
          {t('form.cancel')}
        </Button>
        <Button
          type='submit'
          className='rounded-lg px-3 py-3 text-sm font-medium normal-case text-white'
          disabled={pending}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
